import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '@/config/api';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { TrendingUp, TrendingDown, Calendar, Thermometer, Droplets, Wind, Activity, ThermometerSnowflake, ThermometerSun } from 'lucide-react';
import { readPersisted, writePersisted } from '@/utils/persistence';

interface DailyAverage {
  date: string;
  avg_temperature?: number;
  avg_humidity?: number;
  avg_wind_speed?: number;
}

interface WeatherTrendsProps {
  latitude: number;
  longitude: number;
  days?: number;
  location: string;
}

export function WeatherTrends({ latitude, longitude, days = 7, location }: WeatherTrendsProps) {
  const { t } = useTranslation();
  const [trends, setTrends] = useState<Record<string, any>>({});
  const [dailyData, setDailyData] = useState<DailyAverage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState(days || 7);
  const [chartType, setChartType] = useState<'line' | 'area' | 'composed'>('area');

  useEffect(() => {
    const fetchTrends = async () => {
      setIsLoading(true);
      setError('');
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      const cacheKey = `weatherapp-trends:${latitude}:${longitude}:${selectedPeriod}:${timezone}`;
      const cached = readPersisted<{ trends: Record<string, any>; dailyData: DailyAverage[] } | null>(
        cacheKey,
        null,
      );
      if (cached) {
        setTrends(cached.trends);
        setDailyData(cached.dailyData);
      }

      try {
        // Fetch trends
        const trendsResponse = await fetch(
          `${API_BASE_URL}/weather/trends?lat=${latitude}&lon=${longitude}&days=${selectedPeriod}&timezone=${encodeURIComponent(timezone)}`,
        );
        if (!trendsResponse.ok) throw new Error('Trends konnten nicht geladen werden');
        const trendsData = await trendsResponse.json();

        // Fetch daily averages
        const dailyResponse = await fetch(
          `${API_BASE_URL}/weather/daily-averages?lat=${latitude}&lon=${longitude}&days=${selectedPeriod}&timezone=${encodeURIComponent(timezone)}`,
        );
        if (!dailyResponse.ok) throw new Error('Trenddaten konnten nicht geladen werden');
        const dailyResponseData = await dailyResponse.json();
        const nextTrends = trendsData.trends || {};
        const nextDailyData = dailyResponseData.data || [];
        setTrends(nextTrends);
        setDailyData(nextDailyData);
        writePersisted(cacheKey, { trends: nextTrends, dailyData: nextDailyData });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Fehler beim Laden der Trends');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTrends();
  }, [latitude, longitude, selectedPeriod]);

  // Format data for charts
  const chartData = dailyData.map(day => ({
    date: new Date(day.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }),
    fullDate: day.date,
    temperature: day.avg_temperature ? parseFloat(day.avg_temperature.toFixed(1)) : null,
    humidity: day.avg_humidity ? parseFloat(day.avg_humidity.toFixed(0)) : null,
    wind: day.avg_wind_speed ? parseFloat(day.avg_wind_speed.toFixed(1)) : null,
  }));

  // Calculate trend direction
  const getTrendDirection = (current: number, previous: number) => {
    if (current > previous * 1.05) return 'up';
    if (current < previous * 0.95) return 'down';
    return 'stable';
  };

  const tempTrend = dailyData.length >= 2 
    ? getTrendDirection(
        dailyData[dailyData.length - 1]?.avg_temperature || 0,
        dailyData[0]?.avg_temperature || 0,
      )
    : 'stable';

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-surface p-4 rounded-lg shadow-lg border border-border">
          <p className="font-semibold text-text-primary mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
              <span className="text-text-secondary">{entry.name}:</span>
              <span className="font-semibold text-text-primary">
                {entry.value}
                {entry.dataKey === 'temperature' ? '°C' : entry.dataKey === 'humidity' ? '%' : ' km/h'}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center space-y-3">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto" />
          <p className="text-gray-600 dark:text-gray-400">{t('trends.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl"
      >
        <p className="text-red-600 dark:text-red-400 font-medium">{error}</p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header with controls */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold page-title flex items-center gap-2">
            <Activity className="w-7 h-7 text-blue-600" />
            {t('trends.title')} - {location}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {t('trends.subtitle', { days: selectedPeriod })}
          </p>
        </div>

        <div className="flex gap-2">
          {/* Period selector */}
          <div className="flex button-group-wrapper">
            {[7, 14, 30].map(period => (
              <button
                key={period}
                onClick={() => setSelectedPeriod(period)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  selectedPeriod === period
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'tab-button-inactive'
                }`}
              >
                {period}{t('trends.days')}
              </button>
            ))}
          </div>

          {/* Chart type selector */}
          <div className="flex button-group-wrapper">
            {[
              { type: 'area', label: t('trends.chartTypeArea') },
              { type: 'line', label: t('trends.chartTypeLine') },
              { type: 'composed', label: t('trends.chartTypeMix') },
            ].map(({ type, label }) => (
              <button
                key={type}
                onClick={() => setChartType(type as any)}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  chartType === type
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'tab-button-inactive'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Trends Summary Cards */}
      {Object.keys(trends).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {trends.avg_temperature !== undefined && (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="p-4 bg-linear-to-br from-orange-500 to-red-500 text-white rounded-xl shadow-lg"
            >
              <div className="flex items-center justify-between mb-2">
                <Thermometer className="w-5 h-5 opacity-90" />
                {tempTrend === 'up' && <TrendingUp className="w-4 h-4" />}
                {tempTrend === 'down' && <TrendingDown className="w-4 h-4" />}
              </div>
              <div className="text-xs opacity-90 font-medium">{t('trends.avgTemp')}</div>
              <div className="text-2xl font-bold mt-1">{trends.avg_temperature.toFixed(1)}°C</div>
            </motion.div>
          )}

          {trends.avg_temp_min !== undefined && (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="p-4 bg-linear-to-br from-blue-500 to-cyan-500 text-white rounded-xl shadow-lg"
            >
              <ThermometerSnowflake className="w-5 h-5 opacity-90 mb-2" />
              <div className="text-xs opacity-90 font-medium">{t('trends.avgMin')}</div>
              <div className="text-2xl font-bold mt-1">{trends.avg_temp_min.toFixed(1)}°C</div>
            </motion.div>
          )}

          {trends.avg_temp_max !== undefined && (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="p-4 bg-linear-to-br from-yellow-500 to-orange-500 text-white rounded-xl shadow-lg"
            >
              <ThermometerSun className="w-5 h-5 opacity-90 mb-2" />
              <div className="text-xs opacity-90 font-medium">{t('trends.avgMax')}</div>
              <div className="text-2xl font-bold mt-1">{trends.avg_temp_max.toFixed(1)}°C</div>
            </motion.div>
          )}

          {trends.avg_humidity !== undefined && (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="p-4 bg-linear-to-br from-indigo-500 to-blue-500 text-white rounded-xl shadow-lg"
            >
              <Droplets className="w-5 h-5 opacity-90 mb-2" />
              <div className="text-xs opacity-90 font-medium">{t('trends.avgHumidityShort')}</div>
              <div className="text-2xl font-bold mt-1">{trends.avg_humidity.toFixed(0)}%</div>
            </motion.div>
          )}

          {trends.avg_wind_speed !== undefined && (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="p-4 bg-linear-to-br from-teal-500 to-green-500 text-white rounded-xl shadow-lg"
            >
              <Wind className="w-5 h-5 opacity-90 mb-2" />
              <div className="text-xs opacity-90 font-medium">{t('trends.avgWindSpeedShort')}</div>
              <div className="text-2xl font-bold mt-1">{trends.avg_wind_speed.toFixed(1)} km/h</div>
            </motion.div>
          )}

          {trends.min_temperature !== undefined && trends.max_temperature !== undefined && (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="p-4 bg-linear-to-br from-purple-500 to-pink-500 text-white rounded-xl shadow-lg"
            >
              <Calendar className="w-5 h-5 opacity-90 mb-2" />
              <div className="text-xs opacity-90 font-medium">{t('trends.tempRange')}</div>
              <div className="text-lg font-bold mt-1">
                {trends.min_temperature.toFixed(0)}° - {trends.max_temperature.toFixed(0)}°C
              </div>
            </motion.div>
          )}
        </div>
      )}

      {/* Interactive Charts */}
      {chartData.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="p-6 bg-surface rounded-xl shadow-lg border border-border"
        >
          <h3 className="text-lg font-semibold text-text-primary mb-6">
            {t('trends.tempCourse')}
          </h3>

          <ResponsiveContainer width="100%" height={350}>
            {chartType === 'area' ? (
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorTemp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f97316" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#f97316" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                <XAxis 
                  dataKey="date" 
                  stroke="#6b7280" 
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  stroke="#6b7280" 
                  style={{ fontSize: '12px' }}
                  label={{ value: t('trends.temperatureAxisLabel'), angle: -90, position: 'insideLeft', style: { fill: '#6b7280' } }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="temperature"
                  stroke="#f97316"
                  strokeWidth={3}
                  fill="url(#colorTemp)"
                  name={t('trends.temperatureLabel')}
                />
              </AreaChart>
            ) : chartType === 'line' ? (
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-300 dark:stroke-gray-600" opacity={0.3} />
                <XAxis 
                  dataKey="date" 
                  className="stroke-gray-500 dark:stroke-gray-400"
                  style={{ fontSize: '12px', fill: 'currentColor' }}
                />
                <YAxis 
                  className="stroke-gray-500 dark:stroke-gray-400"
                  style={{ fontSize: '12px', fill: 'currentColor' }}
                  label={{ value: t('trends.temperatureAxisLabel'), angle: -90, position: 'insideLeft', className: 'fill-gray-500 dark:fill-gray-400' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="temperature"
                  stroke="#f97316"
                  strokeWidth={3}
                  dot={{ fill: '#f97316', r: 5 }}
                  activeDot={{ r: 7 }}
                  name={t('trends.temperatureLabel')}
                />
              </LineChart>
            ) : (
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-300 dark:stroke-gray-600" opacity={0.3} />
                <XAxis 
                  dataKey="date" 
                  className="stroke-gray-500 dark:stroke-gray-400"
                  style={{ fontSize: '12px', fill: 'currentColor' }}
                />
                <YAxis 
                  yAxisId="temp"
                  className="stroke-gray-500 dark:stroke-gray-400"
                  style={{ fontSize: '12px', fill: 'currentColor' }}
                  label={{ value: t('trends.temperatureAxisLabel'), angle: -90, position: 'insideLeft', className: 'fill-gray-500 dark:fill-gray-400' }}
                />
                <YAxis 
                  yAxisId="humidity"
                  orientation="right"
                  className="stroke-gray-500 dark:stroke-gray-400"
                  style={{ fontSize: '12px', fill: 'currentColor' }}
                  label={{ value: t('trends.humidityAxisLabel'), angle: 90, position: 'insideRight', className: 'fill-gray-500 dark:fill-gray-400' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area
                  yAxisId="temp"
                  type="monotone"
                  dataKey="temperature"
                  fill="#f9731680"
                  stroke="#f97316"
                  strokeWidth={2}
                  name={t('trends.temperatureLabel')}
                />
                <Line
                  yAxisId="humidity"
                  type="monotone"
                  dataKey="humidity"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6', r: 4 }}
                  name={t('trends.humidityLabel')}
                />
              </ComposedChart>
            )}
          </ResponsiveContainer>
        </motion.div>
      )}

      {/* Wind & Humidity Chart */}
      {chartData.length > 0 && (
        <div className="grid md:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="p-6 bg-surface rounded-xl shadow-lg border border-border"
          >
            <h3 className="page-title text-lg font-semibold mb-6 flex items-center gap-2">
              <Droplets className="w-5 h-5 text-blue-600" />
              {t('trends.humidityChart')}
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-300 dark:stroke-gray-600" opacity={0.3} />
                <XAxis dataKey="date" className="stroke-gray-500 dark:stroke-gray-400" style={{ fontSize: '11px', fill: 'currentColor' }} />
                <YAxis className="stroke-gray-500 dark:stroke-gray-400" style={{ fontSize: '11px', fill: 'currentColor' }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="humidity" fill="#3b82f6" radius={[8, 8, 0, 0]} name={t('trends.humidityLabel')} />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="p-6 bg-surface rounded-xl shadow-lg border border-border"
          >
            <h3 className="page-title text-lg font-semibold mb-6 flex items-center gap-2">
              <Wind className="w-5 h-5 text-teal-600" />
              {t('trends.windChart')}
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorWind" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#14b8a6" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                <XAxis dataKey="date" stroke="#6b7280" style={{ fontSize: '11px' }} />
                <YAxis stroke="#6b7280" style={{ fontSize: '11px' }} />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="wind"
                  stroke="#14b8a6"
                  strokeWidth={2}
                  fill="url(#colorWind)"
                  name={t('trends.windLabel')}
                />
              </AreaChart>
            </ResponsiveContainer>
          </motion.div>
        </div>
      )}

      {/* Data Table */}
      {dailyData.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="overflow-x-auto bg-surface rounded-xl shadow-lg border border-border"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-linear-to-r from-blue-600 to-indigo-600 text-white">
                <th className="p-4 text-left font-semibold">{t('trends.tableDate')}</th>
                <th className="p-4 text-right font-semibold">{t('trends.tableTemp')}</th>
                <th className="p-4 text-right font-semibold">{t('trends.tableHumidity')}</th>
                <th className="p-4 text-right font-semibold">{t('trends.tableWind')}</th>
              </tr>
            </thead>
            <tbody>
              {dailyData.map((day, index) => (
                <motion.tr
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * index }}
                  className="border-b border-border table-row-hover"
                >
                  <td className="p-4 font-medium text-text-primary">
                    {new Date(day.date).toLocaleDateString('de-DE', { 
                      weekday: 'short', 
                      day: '2-digit', 
                      month: '2-digit', 
                    })}
                  </td>
                  <td className="p-4 text-right font-semibold text-orange-600 dark:text-orange-400">
                    {day.avg_temperature?.toFixed(1) || '-'}°C
                  </td>
                  <td className="p-4 text-right font-semibold text-blue-600 dark:text-blue-400">
                    {day.avg_humidity?.toFixed(0) || '-'}%
                  </td>
                  <td className="p-4 text-right font-semibold text-teal-600 dark:text-teal-400">
                    {day.avg_wind_speed?.toFixed(1) || '-'} km/h
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      )}

      {dailyData.length === 0 && !isLoading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="trends-empty-state text-center py-12 rounded-xl border-2 border-dashed"
        >
          <Calendar className="icon w-16 h-16 mx-auto mb-4" />
          <p className="text-primary text-lg font-medium">
            {t('ui.noHistoricalData')}
          </p>
          <p className="text-secondary text-sm mt-2">
            {t('trends.emptyStateText')}
          </p>
        </motion.div>
      )}
    </motion.div>
  );
}
